/**
 * <WorkloadsPanel> — operator-facing workload card grid (#234).
 *
 * Replaced the pod-flat list as the operator's default view. Since JSS#1158
 * it renders on the site's Health tab (`attentionOnly`) and, in full, under
 * Advanced → Workloads. Cards are
 * grouped by namespace and sorted within each namespace so unhealthy
 * workloads bubble to the top. Auto-refreshes every 5s (tighter than the
 * other cluster panels — this is the "is anything broken?" surface).
 *
 * Click-through to a workload drawer is intentionally out of scope for v1;
 * see the issue for the follow-up tracker.
 */
import { useState } from 'react';
import { Box, AlertTriangle } from 'lucide-react';
// Same workaround as RoleGate.tsx — types package is CommonJS, so rollup's
// named-export analysis trips on `exports.X = X` patterns. Namespace import
// + destructure for runtime values, `import type` for type-only refs.
import { Role } from '@jeket-cloud/types/auth';
import type { Workload } from '@/types';
import { RoleGate } from '@/components/auth/RoleGate';
import { useClusterWorkloads } from './useClusterData';
import WorkloadCard from './WorkloadCard';
import EscalationDialog from './EscalationDialog';
import WorkloadDrawer from './WorkloadDrawer';

interface Props {
  siteId: string;
  /**
   * Health tab mode (JSS#1158): list only the workloads that are failing or
   * degraded, with a toggle that shows every workload. The full list is the
   * raw view (arc-systems, buildkit, flux-system…), so it is the default only
   * under Advanced; the operator personas still reach it, and every action on
   * a card or in the drawer (Restart, logs, support bundle), from the toggle.
   */
  attentionOnly?: boolean;
}

const HEALTH_PRIORITY: Record<Workload['healthClass'], number> = {
  failing: 0,
  degraded: 1,
  healthy: 2,
};

function groupByNamespace(workloads: Workload[]): Map<string, Workload[]> {
  const grouped = new Map<string, Workload[]>();
  for (const w of workloads) {
    const arr = grouped.get(w.namespace);
    if (arr) arr.push(w);
    else grouped.set(w.namespace, [w]);
  }
  // Sort within each namespace by health priority (failing first), then name.
  for (const arr of grouped.values()) {
    arr.sort((a, b) => {
      const p = HEALTH_PRIORITY[a.healthClass] - HEALTH_PRIORITY[b.healthClass];
      if (p !== 0) return p;
      return a.name.localeCompare(b.name);
    });
  }
  return grouped;
}

export default function WorkloadsPanel({ siteId, attentionOnly = false }: Props) {
  const { data: workloads, isLoading, error } = useClusterWorkloads(siteId);
  const [showAll, setShowAll] = useState(false);
  const filtering = attentionOnly && !showAll;
  const unhealthy = workloads?.filter((w) => w.healthClass !== 'healthy') ?? [];
  const shown = filtering ? unhealthy : workloads;
  const [escalationOpen, setEscalationOpen] = useState(false);
  const [escalationToast, setEscalationToast] = useState<string | null>(null);
  // Drawer state lifted from individual cards (#241) so we mount one
  // WorkloadDrawer at the panel level rather than one per card.
  const [drawerWorkload, setDrawerWorkload] = useState<Workload | null>(null);

  /**
   * The "Page on-call" button (#237). Wrapped in <RoleGate> so non-operator
   * personas don't see it; the BFF and deployment-service mirror also
   * enforce the same gate. Defense in depth.
   */
  const escalationButton = (
    <RoleGate role={Role.EventOperator}>
      <button
        onClick={() => setEscalationOpen(true)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-status-error/10 hover:bg-status-error/20 text-status-error border border-status-error/30 transition-colors"
        data-testid="page-on-call-button"
      >
        <AlertTriangle size={14} />
        Page on-call
      </button>
    </RoleGate>
  );

  const header = (
    <div className="flex items-center justify-between gap-3">
      <h2 className="text-sm font-medium text-silver-muted">
        {filtering ? 'Workloads needing attention' : 'Workloads'}
      </h2>
      <div className="flex items-center gap-2">
        {attentionOnly && workloads && workloads.length > 0 && (
          <button
            onClick={() => setShowAll((v) => !v)}
            className="px-3 py-1.5 text-xs text-silver-muted border border-surface-elevated rounded-lg hover:text-silver-bright hover:bg-surface-elevated transition-colors"
            data-testid="workloads-show-all-toggle"
          >
            {showAll ? 'Show only workloads needing attention' : `Show all workloads (${workloads.length})`}
          </button>
        )}
        {escalationToast && (
          <span className="text-xs text-status-success" role="status" aria-live="polite">
            On-call paged. Reference: {escalationToast}
          </span>
        )}
        {escalationButton}
      </div>
    </div>
  );

  // Modal + transient toast surface; rendered alongside every body branch.
  const dialog = escalationOpen && (
    <EscalationDialog
      siteId={siteId}
      onClose={() => setEscalationOpen(false)}
      onSuccess={(referenceId) => {
        setEscalationToast(referenceId);
        // Clear the toast after ~10s — long enough to copy the ref id.
        window.setTimeout(() => setEscalationToast(null), 10_000);
      }}
    />
  );

  let body: JSX.Element;
  if (isLoading && !workloads) {
    body = (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="bg-surface border border-surface-elevated rounded-xl p-4 animate-pulse">
            <div className="h-4 bg-surface-elevated rounded w-2/3 mb-2" />
            <div className="h-3 bg-surface-elevated rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  } else if (error) {
    body = (
      <div className="bg-surface border border-status-error/30 rounded-xl p-6 text-center">
        <p className="text-status-error text-sm">Failed to load workloads</p>
        <p className="text-xs text-silver-muted mt-1">{(error as Error).message}</p>
      </div>
    );
  } else if (filtering && workloads && workloads.length > 0 && unhealthy.length === 0) {
    body = (
      <div className="bg-surface border border-surface-elevated rounded-xl text-center py-8" data-testid="workloads-all-healthy">
        <p className="text-silver-muted">All {workloads.length} workloads are healthy</p>
      </div>
    );
  } else if (!shown || shown.length === 0) {
    body = (
      <div className="bg-surface border border-surface-elevated rounded-xl text-center py-12 space-y-3">
        <Box size={40} className="mx-auto text-silver-muted/30" />
        <p className="text-silver-muted">No workloads in user namespaces yet</p>
      </div>
    );
  } else {
    const grouped = groupByNamespace(shown);
    const namespaces = Array.from(grouped.keys()).sort();
    body = (
      <div className="space-y-6">
        {namespaces.map((ns) => {
          const items = grouped.get(ns)!;
          return (
            <section key={ns} className="space-y-3">
              <h3 className="text-xs font-medium text-silver-muted uppercase tracking-wider">
                {ns} <span className="text-silver-muted/50">({items.length})</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {items.map((w) => (
                  <WorkloadCard
                    key={`${w.kind}/${w.namespace}/${w.name}`}
                    workload={w}
                    siteId={siteId}
                    onClick={setDrawerWorkload}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {header}
      {body}
      {dialog}
      <WorkloadDrawer
        workload={drawerWorkload}
        siteId={siteId}
        onClose={() => setDrawerWorkload(null)}
      />
    </div>
  );
}
